import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './app.css'; 

/* --- Page Imports --- */
import HomePage from './pages/Homepage';
import ContactPage from './pages/ContactPage';
import RegisterPage from './pages/RegisterPage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import GalleryPage from './pages/GalleryPage';
import EventsPage from './pages/EventsPage';
import EventRegisterPage from './pages/EventRegisterPage';
import FAQPage from './pages/FAQPage';
import MyAccountPage from './pages/MyAccountPage';
import VerifyCodePage from './pages/VerifyCodePage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import ServiceCheckoutPage from './pages/ServiceCheckoutPage';
import AccountServiceDetailPage from './pages/AccountServiceDetailPage';
import StarterAnimaPage from './pages/StarterAnimaPage';
import HelpPage from './pages/HelpPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Dashboard from './pages/Dashboard'; 
import BookingSelectionPage from './pages/BookingSelectionPage'; 
import OffersPage from './pages/OffersPage';

import LanguageSwitcher from './components/LanguageSwitcher';

/* ------------ Global Styles ------------- */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;700&family=Cinzel+Decorative:wght@700;900&family=Montserrat:wght@300;400;500;600&display=swap');

    :root {
      --c-gold: #FFA500;
      --c-dark: #1a1a1a;
      --c-darker: #050505;
      --c-light: #ffffff;
      --c-gray-bg: #f9fafb;
      --font-heading: 'Cinzel', serif;
      --font-body: 'Montserrat', sans-serif;
      --font-logo: 'Cinzel Decorative', cursive;
      --marquee-height: 32px;
      --header-height: 80px;
    }

    body {
      font-family: var(--font-body);
      margin: 0; padding: 0;
      background-color: var(--c-gray-bg);
      color: black;
      -webkit-font-smoothing: antialiased;
    }
    h1, h2, h3, h4 { font-family: var(--font-heading); letter-spacing: 0.02em; }

    /* Page Animation */
    @keyframes fadeUp { 0% { opacity: 0; transform: translateY(20px); } 100% { opacity: 1; transform: translateY(0); } }
    
    .animate-fadeUp { 
        animation: fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        width: 100%; position: relative; z-index: 1; 
        padding-top: calc(var(--marquee-height) + var(--header-height)); 
    }

    .animate-dashboard {
        animation: fadeUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        width: 100%; position: relative; z-index: 1; 
        padding-top: 0; 
    }

    /* Utilities */
    .desktop-only { display: none; }
    @media (min-width: 1200px) { .desktop-only { display: inline; } }

    /* Marquee */
    .shivba-marquee { position: fixed; top: 0; left: 0; width: 100%; height: var(--marquee-height); z-index: 1001; background: var(--c-dark); color: white; display: flex; font-size: 0.85rem; border-bottom: 1px solid #333; overflow: hidden; }
    .shivba-marquee-label { background: var(--c-gold); color: black; padding: 0 20px; display: flex; align-items: center; font-weight: 800; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.1em; position: relative; z-index: 2; box-shadow: 4px 0 10px rgba(0,0,0,0.3); height: 100%; }
    .shivba-marquee-window { flex: 1; overflow: hidden; display: flex; align-items: center; position: relative; }
    .shivba-marquee-track { display: flex; gap: 40px; white-space: nowrap; padding-left: 20px; font-family: var(--font-body); font-weight: 500; animation: marquee 20s linear infinite; }
    @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }

    /* --- UPDATED HEADER STYLES (BLACK BLUR) --- */
    .shivba-header { 
        background: rgba(0, 0, 0, 0.85); /* Black background with transparency */
        backdrop-filter: blur(12px); 
        border-bottom: 1px solid rgba(255, 255, 255, 0.1); /* Subtle light border */
        position: fixed; top: var(--marquee-height); left: 0; width: 100%; height: var(--header-height); z-index: 1000; transition: all 0.3s ease; box-shadow: 0 2px 10px rgba(0,0,0,0.2); 
    }
    .shivba-header-inner { max-width: 1400px; margin: 0 auto; padding: 0 30px; height: 100%; display: flex; align-items: center; justify-content: space-between; }
    
    .shivba-logo { 
        font-family: var(--font-logo); font-size: 2rem; font-weight: 900; 
        color: white; /* Changed to White */
        cursor: pointer; text-transform: uppercase; letter-spacing: 0.15em; text-shadow: 0 2px 4px rgba(0,0,0,0.3); transition: transform 0.3s ease; 
    }
    .shivba-logo:hover { transform: scale(1.02); }
    
    .shivba-nav { display: flex; gap: 20px; align-items: center; }
    
    .nav-btn { 
        background: none; border: none; font-family: var(--font-body); font-size: 0.85rem; font-weight: 700; 
        color: white; /* Changed to White */
        cursor: pointer; transition: all 0.3s; text-transform: uppercase; letter-spacing: 0.05em; position: relative; white-space: nowrap; 
    }
    .nav-btn::after { content: ''; position: absolute; bottom: -4px; left: 0; width: 0; height: 2px; background: var(--c-gold); transition: width 0.3s ease; }
    .nav-btn:hover::after, .nav-btn.active::after { width: 100%; }
    .nav-btn:hover, .nav-btn.active { color: var(--c-gold); } /* Hover turns Gold */
    
    /* New Offers Badge in Nav */
    .nav-btn.offers-btn { color: #ea580c; display: flex; align-items: center; gap: 5px; }
    .nav-btn.offers-btn::before { content: '🎁'; font-size: 1rem; }
    .nav-btn.offers-btn:hover { color: #ff7b2e; } /* Lighter orange on hover */
    .nav-btn.offers-btn::after { background: #ea580c; }

    .shivba-header-actions { display: flex; align-items: center; gap: 15px; }
    
    .shivba-primary-btn { background: white; color: black; border: none; padding: 12px 24px; border-radius: 4px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; font-size: 0.8rem; cursor: pointer; transition: all 0.3s; box-shadow: 0 4px 10px rgba(0,0,0,0.2); white-space: nowrap; }
    .shivba-primary-btn:hover { background: var(--c-gold); color: black; transform: translateY(-2px); }
    
    .shivba-ghost-btn { 
        background: transparent; 
        border: 1px solid rgba(255, 255, 255, 0.5); /* Light border */
        padding: 10px 20px; border-radius: 4px; 
        color: white; /* Changed to White */
        cursor: pointer; font-size: 0.8rem; display: flex; align-items: center; gap: 8px; font-weight: 600; text-transform: uppercase; transition: all 0.3s; white-space: nowrap; 
    }
    .shivba-ghost-btn:hover { background: white; color: black; border-color: white; }

    /* --- SIDE DRAWER MOBILE MENU --- */
    .shivba-menu-toggle { display: none; flex-direction: column; gap: 5px; background: none; border: none; cursor: pointer; padding: 5px; z-index: 1002; }
    .shivba-menu-toggle span { display: block; width: 25px; height: 2px; background-color: white; /* Changed to White */ transition: all 0.3s; }

    .shivba-mobile-overlay {
        position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
        background: rgba(0,0,0,0.6); backdrop-filter: blur(4px);
        z-index: 10005; opacity: 0; visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s;
    }
    .shivba-mobile-overlay.visible { opacity: 1; visibility: visible; }

    .shivba-mobile-drawer {
        position: fixed; top: 0; right: 0; width: 80%; max-width: 320px; height: 100%;
        background: white; z-index: 10010;
        transform: translateX(100%); transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        box-shadow: -5px 0 25px rgba(0,0,0,0.15); display: flex; flex-direction: column;
    }
    .shivba-mobile-drawer.open { transform: translateX(0); }

    .drawer-header { padding: 20px 25px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #eee; background: #fcfcfc; }
    .drawer-title { font-family: var(--font-heading); font-weight: 700; font-size: 1.2rem; color: var(--c-dark); }
    .drawer-close-btn { background: none; border: 1px solid #ddd; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; color: #666; cursor: pointer; transition: all 0.2s; }
    .drawer-close-btn:hover { background: black; color: white; border-color: black; }

    .drawer-content { flex: 1; overflow-y: auto; padding: 10px 0; display: flex; flex-direction: column; }
    .drawer-link { text-align: left; background: none; border: none; padding: 16px 30px; font-family: var(--font-body); font-size: 1rem; font-weight: 500; color: #333; border-bottom: 1px solid #f5f5f5; cursor: pointer; transition: all 0.2s; }
    .drawer-link:hover { background: #fafafa; color: var(--c-gold); padding-left: 35px; }
    .drawer-link.active { color: black; font-weight: 700; border-left: 4px solid var(--c-gold); background: #fffdf5; }

    .drawer-footer { padding: 25px; border-top: 1px solid #eee; background: #fafafa; display: flex; flex-direction: column; gap: 15px; }
    .drawer-cta-btn { width: 100%; background: var(--c-gold); color: black; border: none; padding: 14px; font-weight: 700; text-transform: uppercase; cursor: pointer; }

    /* --- PROFESSIONAL FOOTER --- */
    .shivba-footer { background: #111111; color: #999; padding: 5rem 2rem 2rem; font-size: 0.9rem; border-top: 3px solid var(--c-gold); position: relative; z-index: 2; }
    .shivba-footer-inner { max-width: 1400px; margin: 0 auto; display: grid; grid-template-columns: 2fr 1fr 1fr 1.5fr; gap: 4rem; }
    
    .shivba-footer-logo { font-family: var(--font-logo); font-size: 2.8rem; color: white; margin-bottom: 1rem; opacity: 1; letter-spacing: 0.1em; }
    .shivba-footer-text { margin-bottom: 1.5rem; line-height: 1.7; max-width: 320px; color: #aaa; font-size: 0.95rem; }
    
    /* --- UPDATED SOCIAL LINKS (PILL SHAPE WITH TEXT) --- */
    .shivba-footer-social { display: flex; gap: 15px; margin-top: 1.5rem; flex-wrap: wrap; }
    
    .shivba-social-link { 
        display: flex; align-items: center; justify-content: center; 
        width: auto; height: auto; padding: 10px 20px; /* Pill padding */
        border-radius: 50px; /* Rounded pill shape */
        background: rgba(255,255,255,0.08); 
        color: white; 
        text-decoration: none; 
        border: 1px solid rgba(255,255,255,0.05); 
        transition: all 0.3s ease; 
        font-weight: 600;
        font-size: 0.85rem;
        letter-spacing: 0.05em;
    }

    /* --- ATTRACTIVE & LIMITED (MINIMALIST) FOOTER INPUT --- */

.shivba-footer-input-wrap { 
    display: flex !important;
    flex-direction: column !important; /* Stacked layout */
    gap: 12px !important; /* Clean space between input and button */
    margin-top: 1.5rem !important;
    max-width: 100% !important; /* Uses full column width but keeps it neat */
}

/* 1. The Input - Clean, Dark, Matte */
.shivba-footer-input-wrap input { 
    width: 100% !important;
    padding: 16px 20px !important;
    
    background: #1a1a1a !important; /* Solid matte dark grey */
    border: 1px solid #333 !important; /* Subtle border */
    border-radius: 4px !important; /* Slightly rounded */
    
    color: #fff !important;
    font-family: var(--font-body);
    font-size: 0.9rem !important;
    outline: none !important;
    transition: border-color 0.3s ease !important;
    box-shadow: none !important; /* No shadow/glow */
}

/* Input Focus - Only the border changes color */
.shivba-footer-input-wrap input:focus {
    border-color: var(--c-gold) !important; /* Minimal gold accent */
    background: #222 !important;
}

/* 2. The Button - Solid Gold, Bold, No Fancy Effects */
.shivba-footer-subscribe { 
    width: 100% !important; /* Full width to match input */
    padding: 16px !important;
    
    background: var(--c-gold) !important; /* Your Brand Gold */
    color: #000 !important; /* Black Text */
    
    border: none !important;
    border-radius: 4px !important;
    
    font-weight: 800 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.1em !important;
    font-size: 0.85rem !important;
    cursor: pointer !important;
    transition: background 0.3s ease !important;
    box-shadow: none !important; /* No shadow */
}

/* Button Hover - Just slightly lighter */
.shivba-footer-subscribe:hover { 
    background: #ffb732 !important; /* Lighter Gold */
    transform: none !important; /* No movement, keeps it "limited" */
}
    
    
    .shivba-social-link svg { width: 18px; height: 18px; fill: currentColor; margin-right: 10px; }
    
    .shivba-social-link:hover { background: var(--c-gold); color: black; border-color: var(--c-gold); transform: translateY(-3px); }

    .shivba-footer-col h4 { color: white; margin-bottom: 1.8rem; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.25em; font-weight: 700; position: relative; display: inline-block; }
    .shivba-footer-col h4::after { content: ''; position: absolute; bottom: -8px; left: 0; width: 30px; height: 2px; background: var(--c-gold); }

    .shivba-footer-col button, .shivba-footer-col a { display: block; width: 100%; padding: 12px 16px; margin-bottom: 8px; background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.05); color: #bbb; text-align: left; cursor: pointer; transition: all 0.4s cubic-bezier(0.25, 1, 0.5, 1); position: relative; overflow: hidden; font-size: 0.85rem; font-family: var(--font-body); text-decoration: none; border-radius: 0px; }
    .shivba-footer-col button:hover, .shivba-footer-col a:hover { background: var(--c-gold); border-color: var(--c-gold); color: black; transform: translateX(8px); padding-left: 24px; box-shadow: -4px 4px 0 rgba(255, 255, 255, 0.1); }
    
    .shivba-footer-col p { margin-bottom: 1rem; color: #bbb; }
    .shivba-footer-input-wrap { display: flex; gap: 0; margin-top: 1.5rem; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; overflow: hidden; transition: border-color 0.3s; }
    .shivba-footer-input-wrap:focus-within { border-color: var(--c-gold); background: black; }
    .shivba-footer-input-wrap input { padding: 16px 20px; border: none; background: transparent; color: white; flex: 1; outline: none; font-family: var(--font-body); font-size: 0.9rem; }
    .shivba-footer-subscribe { background: var(--c-gold); color: black; border: none; padding: 0 30px; cursor: pointer; font-weight: 700; text-transform: uppercase; font-size: 0.8rem; letter-spacing: 0.1em; transition: all 0.3s ease; }
    .shivba-footer-subscribe:hover { background: white; color: black; }
    .shivba-footer-bottom { max-width: 1400px; margin: 5rem auto 0; padding-top: 2rem; border-top: 1px solid rgba(255,255,255,0.08); display: flex; justify-content: space-between; color: #666; font-size: 0.8rem; letter-spacing: 0.05em; }

    /* FAB */
    .fab-container { position: fixed; bottom: 30px; right: 30px; z-index: 9999; display: flex; flex-direction: column; align-items: center; gap: 15px; }
    .fab-main { width: 60px; height: 60px; border-radius: 50%; background: var(--c-gold); color: black; border: none; font-size: 24px; cursor: pointer; box-shadow: 0 10px 25px rgba(255, 165, 0, 0.4); transition: transform 0.3s; display: flex; align-items: center; justify-content: center; }
    .fab-main:hover { transform: scale(1.1) rotate(90deg); }
    .fab-main.active { transform: rotate(45deg); background: var(--c-dark); color: white; box-shadow: 0 10px 25px rgba(0,0,0,0.5); }
    .fab-item { width: 48px; height: 48px; border-radius: 50%; background: white; color: var(--c-dark); border: none; font-size: 18px; cursor: pointer; box-shadow: 0 5px 15px rgba(0,0,0,0.1); opacity: 0; transform: translateY(20px) scale(0.8); transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); position: relative; }
    .fab-container.open .fab-item { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }
    .fab-item::after { content: attr(data-tooltip); position: absolute; right: 60px; background: rgba(0,0,0,0.8); color: white; padding: 5px 10px; border-radius: 4px; font-size: 11px; white-space: nowrap; opacity: 0; pointer-events: none; transition: opacity 0.2s; top: 12px; font-family: var(--font-body); font-weight: 600; }
    .fab-item:hover::after { opacity: 1; }

    body.dark-mode { background-color: #050505 !important; color: #e0e0e0 !important; }
    body.dark-mode .shivba-header { background-color: rgba(0, 0, 0, 0.95); border-bottom: 1px solid #333; }
    body.dark-mode .shivba-logo { color: white; text-shadow: 0 2px 10px rgba(255, 165, 0, 0.3); }
    body.dark-mode .nav-btn { color: white; }
    body.dark-mode .nav-btn:hover, body.dark-mode .nav-btn.active { color: var(--c-gold); }
    body.dark-mode .shivba-ghost-btn { border-color: white; color: white; }
    body.dark-mode .shivba-ghost-btn:hover { border-color: white; background: white; color: black; }
    body.dark-mode .shivba-primary-btn { background: var(--c-gold); color: black; }
    body.dark-mode .shivba-primary-btn:hover { background: white; }
    
    body.dark-mode .shivba-menu-toggle span { background-color: white; }
    body.dark-mode .shivba-mobile-drawer { background: #1a1a1a; border-left: 1px solid #333; }
    body.dark-mode .drawer-header { background: #111; border-color: #333; }
    body.dark-mode .drawer-title { color: white; }
    body.dark-mode .drawer-content { background: #1a1a1a; }
    body.dark-mode .drawer-link { color: #ccc; border-color: #2a2a2a; }
    body.dark-mode .drawer-link:hover { background: #222; color: var(--c-gold); }
    body.dark-mode .drawer-link.active { background: #222; color: white; }
    body.dark-mode .drawer-footer { background: #111; border-color: #333; }

    @media (max-width: 1024px) {
      .shivba-nav { display: none; } 
      .shivba-header-inner { justify-content: space-between; }
      .shivba-menu-toggle { display: flex; } 
      .shivba-footer-inner { grid-template-columns: 1fr; gap: 3rem; text-align: center; }
      .shivba-footer-social { justify-content: center; }
      .shivba-footer-logo { margin: 0 auto 1.5rem; display: block; }
      .shivba-footer-bottom { flex-direction: column; gap: 15px; text-align: center; }
      .shivba-footer-col button, .shivba-footer-col a { text-align: center; }
      .shivba-footer-col h4::after { left: 50%; transform: translateX(-50%); } 
    }

    /* --- PREMIUM SOCIAL BUTTONS (GLASS + GLOW) --- */

.shivba-footer-social { 
    display: flex !important;
    flex-direction: column !important;
    gap: 16px !important; /* Increased spacing for cleaner look */
    width: 100% !important;
    margin-top: 24px !important;
}

.shivba-social-link { 
    position: relative; /* Needed for the glow effect */
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    width: 100% !important;
    padding: 18px 24px !important;
    
    /* Dark Glass Background */
    background: linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%) !important;
    backdrop-filter: blur(10px);
    
    /* Subtle Border */
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
    border-radius: 12px !important; /* Softer, modern corners */
    
    color: #e0e0e0 !important;
    font-family: var(--font-body);
    font-weight: 600 !important;
    font-size: 0.95rem !important;
    letter-spacing: 0.05em;
    text-decoration: none !important;
    
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important; /* Bouncy transition */
    overflow: hidden; /* Contains the glow */
}

/* Icon Styling */
.shivba-social-link svg {
    margin-right: 18px !important;
    width: 22px !important;
    height: 22px !important;
    fill: #888 !important; /* Muted default color */
    transition: all 0.3s ease !important;
}

/* --- HOVER EFFECTS --- */

.shivba-social-link:hover {
    transform: translateY(-4px) !important; /* Lifts up */
    background: linear-gradient(145deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%) !important;
    border-color: rgba(255, 165, 0, 0.5) !important; /* Gold border appears */
    box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.3), 
                0 0 15px -5px rgba(255, 165, 0, 0.3) !important; /* Shadow + Gold Glow */
    color: white !important;
}

.shivba-social-link:hover svg {
    fill: var(--c-gold) !important; /* Icon turns Gold */
    transform: scale(1.15) rotate(5deg) !important; /* Slight rotation */
}

/* Optional: Add a subtle arrow indicator on hover */
.shivba-social-link::after {
    content: '→';
    position: absolute;
    right: 24px;
    opacity: 0;
    transform: translateX(-10px);
    color: var(--c-gold);
    transition: all 0.3s ease;
}

.shivba-social-link:hover::after {
    opacity: 1;
    transform: translateX(0);
}


  `}</style>
);

/* ------------ Components ------------- */

function Header({ setPage, activePage }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; } 
    else { document.body.style.overflow = 'unset'; }
  }, [open]);

  const goto = (name) => {
    setPage({ name });
    setOpen(false); 
  };

  const navClass = (name) => activePage === name ? 'nav-btn active' : 'nav-btn';
  const mobileNavClass = (name) => activePage === name ? 'drawer-link active' : 'drawer-link';

  return (
    <header className="shivba-header">
      <div className="shivba-header-inner">
        <div className="shivba-logo" onClick={() => goto('home')}>SHIVBA</div>

        {/* DESKTOP NAVIGATION */}
        <nav className="shivba-nav">
          <button className={navClass('home')} onClick={() => goto('home')}>{t('nav.home') || 'Home'}</button>
          
          {/* MOVED OFFERS HERE to ensure visibility */}
          <button className={`${navClass('offers')} offers-btn`} onClick={() => goto('offers')}>
              Offers
          </button>

          <button className={navClass('about')} onClick={() => goto('about')}>{t('nav.about') || 'About'}</button>
          <button className={navClass('services')} onClick={() => goto('services')}>{t('nav.services') || 'Services'}</button>
          <button className={navClass('events')} onClick={() => goto('events')}>{t('nav.events') || 'Events'}</button>
          <button className={navClass('gallery')} onClick={() => goto('gallery')}>{t('nav.gallery') || 'Gallery'}</button>
          <button className={navClass('faq')} onClick={() => goto('faq')}>{t('nav.faq') || 'FAQ'}</button>
          <button className={navClass('contact')} onClick={() => goto('contact')}>{t('nav.contact') || 'Contact'}</button>
        </nav>

        <div className="shivba-header-actions">
          <div className="desktop-only"><LanguageSwitcher /></div>
          
          <button className="shivba-ghost-btn" onClick={() => goto('account')}>
            <span>👤</span> <span className="desktop-only">{t('nav.myAccount') || 'My Account'}</span>
          </button>

          <button className="shivba-primary-btn desktop-only" onClick={() => goto('register')}>
            {t('nav.register') || 'Register'}
          </button>
          
          <button className={`shivba-menu-toggle ${open ? 'open' : ''}`} onClick={() => setOpen(true)} aria-label="Open navigation">
            <span /> <span /> <span />
          </button>
        </div>
      </div>

      {/* MOBILE DRAWER */}
      <div className={`shivba-mobile-overlay ${open ? 'visible' : ''}`} onClick={() => setOpen(false)} />
      <div className={`shivba-mobile-drawer ${open ? 'open' : ''}`}>
        <div className="drawer-header">
           <div className="shivba-logo" onClick={() => goto('home')} style={{ fontSize: '1.5rem', cursor: 'pointer', color: '#1a1a1a' }}>SHIVBA</div>
           <button className="drawer-close-btn" onClick={() => setOpen(false)}>✕</button>
        </div>

        <div className="drawer-content">
          <button className={mobileNavClass('home')} onClick={() => goto('home')}>{t('nav.home') || 'Home'}</button>
          {/* OFFERS IN MOBILE MENU */}
          <button className={mobileNavClass('offers')} onClick={() => goto('offers')} style={{color: '#ea580c', fontWeight: 'bold'}}>Offers</button>
          
          <button className={mobileNavClass('about')} onClick={() => goto('about')}>{t('nav.about') || 'About'}</button>
          <button className={mobileNavClass('services')} onClick={() => goto('services')}>{t('nav.services') || 'Services'}</button>
          <button className={mobileNavClass('events')} onClick={() => goto('events')}>{t('nav.events') || 'Events'}</button>
          <button className={mobileNavClass('gallery')} onClick={() => goto('gallery')}>{t('nav.gallery') || 'Gallery'}</button>
          <button className={mobileNavClass('faq')} onClick={() => goto('faq')}>{t('nav.faq') || 'FAQ'}</button>
          <button className={mobileNavClass('help')} onClick={() => goto('help')}>{t('nav.help') || 'Help'}</button>
          <button className={mobileNavClass('contact')} onClick={() => goto('contact')}>{t('nav.contact') || 'Contact'}</button>
        </div>

        <div className="drawer-footer">
           <LanguageSwitcher />
           <button className="drawer-cta-btn" onClick={() => goto('register')}>{t('nav.register') || 'Register'}</button>
        </div>
      </div>
    </header>
  );
}

function MarqueeBar() {
  const { t } = useTranslation();
  return (
    <div className="shivba-marquee">
      <div className="shivba-marquee-label">{t('hero.latestUpdates') || 'LATEST'}</div>
      <div className="shivba-marquee-window">
        <div className="shivba-marquee-track">
          <span>{t('hero.tickerText') || 'Welcome to Shivba Group'}</span>
          <span>•</span>
          <span>Admissions Open for 2025 Batch</span>
          <span>•</span>
          <span>New Hostel Wing Opening Soon</span>
          <span>•</span>
          <span>{t('hero.tickerText') || 'Welcome to Shivba Group'}</span>
        </div>
      </div>
    </div>
  );
}

function Modal({ show, title, message, content, type, onClose }) {
  if (!show) return null;
  return (
    <div className="modal-overlay" onClick={onClose} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 10000,
      display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(5px)'
    }}>
      <div className={`modal ${type}`} onClick={(e) => e.stopPropagation()} style={{ 
          background: 'white', padding: '2.5rem', borderRadius: '16px', maxWidth: '500px', width: '90%',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <h2 style={{marginTop:0, fontFamily:'Cinzel, serif', fontSize:'1.8rem', color:'#1a1a1a'}}>{title}</h2>
        {content ? content : <p style={{lineHeight:1.6, color:'#555', fontFamily:'Montserrat, sans-serif'}}>{message}</p>}
        <div style={{ marginTop: '25px', textAlign: 'right' }}>
          <button onClick={onClose} className="shivba-primary-btn">Close</button>
        </div>
      </div>
    </div>
  );
}

function Footer({ setPage }) {
  const handleSubscribeClick = () => {
    setPage({ name: 'register' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="shivba-footer">
      <div className="shivba-footer-inner">
        <div className="shivba-footer-col brand">
          <div className="shivba-footer-logo">SHIVBA</div>
          <p className="shivba-footer-text">Forging legacy through strength, culture, and community excellence. Join the revolution today.</p>
          <div className="shivba-footer-social">
            {/* Instagram 1 */}
            <a href="https://www.instagram.com/shivbastalim/" target="_blank" rel="noopener noreferrer" className="shivba-social-link insta" title="Instagram Main">
                <svg viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.069-4.85.069-3.204 0-3.584-.012-4.849-.069-3.204 0-3.584-.012-4.849-.069-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948-0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                <span>Instagram</span>
            </a>
            {/* Instagram 2 */}
            <a href="https://www.instagram.com/socialawarenessfoundation/" target="_blank" rel="noopener noreferrer" className="shivba-social-link insta" title="Instagram Secondary">
                <svg viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.069-4.85.069-3.204 0-3.584-.012-4.849-.069-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948-0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                <span>Social Wing</span>
            </a>
            {/* Facebook */}
            <a href="https://www.facebook.com/profile.php?id=100017188563264" target="_blank" rel="noopener noreferrer" className="shivba-social-link fb" title="Facebook">
                <svg viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.791-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                <span>Facebook</span>
            </a>
          </div>
        </div>
        <div className="shivba-footer-col">
          <h4>Explore</h4>
          <button onClick={() => setPage({ name: 'home' })}>Home</button>
          <button onClick={() => setPage({ name: 'about' })}>About Us</button>
          <button onClick={() => setPage({ name: 'services' })}>Services</button>
          <button onClick={() => setPage({ name: 'events' })}>Events</button>
          <button onClick={() => setPage({ name: 'gallery' })}>Gallery</button>
          <button onClick={() => setPage({ name: 'faq' })}>FAQ</button>
        </div>
        <div className="shivba-footer-col">
          <h4>Contact</h4>
          <p>Jadhav Commercial Centre, Manik Chowk, Chakan – 410501, Pune.</p>
          <p>revolution2020.saf@gmail.com</p>
          <p>+91 97672 34353</p>
        </div>
        <div className="shivba-footer-col newsletter">
          <h4>Stay Connected</h4>
          <p>Receive updates on new batches and community events.</p>
          <div className="shivba-footer-input-wrap">
            <input type="email" placeholder="Your email address" />
            <button className="shivba-footer-subscribe" onClick={handleSubscribeClick}>Join</button>
          </div>
        </div>
      </div>
      <div className="shivba-footer-bottom">
        <span>© {new Date().getFullYear()} Shivba Group. All rights reserved.</span>
        <span className="shivba-footer-bottom-right">Designed with precision in Pune, India.</span>
      </div>
    </footer>
  );
}

/* ------------ App ------------- */
function App() {
  const [page, setPage] = useState(() => {
    const path = window.location.pathname;
    if (path === '/reset-password') return { name: 'reset-password' };
    
    const hasSeenIntro = sessionStorage.getItem('shivba_intro_shown');
    if (!hasSeenIntro) {
      sessionStorage.setItem('shivba_intro_shown', 'true');
      return { name: 'starter-anima' };
    }

    try {
      const savedPage = localStorage.getItem('shivba_page');
      if (savedPage) {
        const parsed = JSON.parse(savedPage);
        if (parsed && parsed.name && parsed.name !== 'starter-anima') return parsed;
      }
    } catch (e) { console.error("Page restore failed", e); }

    return { name: 'home' };
  });

  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [modalState, setModalState] = useState({ show: false, title: '', message: '', content: null, type: 'info' });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('shivba_theme') === 'dark'; } catch { return false; }
  });

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const isFirstRender = useRef(true);
  const [accountMember, setAccountMember] = useState(null);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark-mode');
      localStorage.setItem('shivba_theme', 'dark');
    } else {
      document.body.classList.remove('dark-mode');
      localStorage.setItem('shivba_theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode((prev) => !prev);
  const showShortcuts = () => { setPage({ name: 'help' }); setSettingsOpen(false); };
  const scrollToTop = () => { window.scrollTo({ top: 0, behavior: 'smooth' }); setSettingsOpen(false); };

  const handleSetPage = useCallback((nextPage) => {
    if (page.name === nextPage.name && JSON.stringify(page.params) === JSON.stringify(nextPage.params)) return;
    setHistory((prev) => [...prev, page]);
    setFuture([]);
    setPage(nextPage);
  }, [page]);

  const goBack = useCallback(() => {
    if (history.length === 0) return;
    const previousPage = history[history.length - 1];
    setFuture((prev) => [...prev, page]);
    setHistory(history.slice(0, -1));
    setPage(previousPage);
  }, [history, page]);

  const goForward = useCallback(() => {
    if (future.length === 0) return;
    const nextPage = future[future.length - 1];
    setHistory((prev) => [...prev, page]);
    setFuture(future.slice(0, -1));
    setPage(nextPage);
  }, [future, page]);

  const closeModal = useCallback(() => {
    setModalState({ show: false, title: '', message: '', content: null, type: 'info' });
  }, []);

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    window.scrollTo(0, 0);
  }, [page.name]);

  useEffect(() => {
    if (page.name !== 'starter-anima') {
      try { localStorage.setItem('shivba_page', JSON.stringify(page)); } catch {}
    }
  }, [page]);

  // --- GLOBAL KEYBOARD SHORTCUTS ---
  useEffect(() => {
    const handleGlobalKeys = (e) => {
      if (e.key === 'Escape') closeModal();
      if (document.activeElement && ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName) && !e.altKey && !e.ctrlKey) return;

      if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'Backspace')) { e.preventDefault(); goBack(); }
      if (e.altKey && (e.key === 'ArrowRight')) { e.preventDefault(); goForward(); }
      
      if (e.altKey) {
        if(e.key.toLowerCase() === 'h') handleSetPage({ name: 'home' });
        if(e.key.toLowerCase() === 'a') handleSetPage({ name: 'about' });
        if(e.key.toLowerCase() === 's') handleSetPage({ name: 'services' });
        if(e.key.toLowerCase() === 'e') handleSetPage({ name: 'events' });
        if(e.key.toLowerCase() === 'g') handleSetPage({ name: 'gallery' });
        if(e.key.toLowerCase() === 'r') handleSetPage({ name: 'register'});
        if(e.key.toLowerCase() === 'c') handleSetPage({ name: 'contact' });
      }

      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        handleSetPage({ name: 'dashboard' });
      }
    };
    window.addEventListener('keydown', handleGlobalKeys);
    return () => window.removeEventListener('keydown', handleGlobalKeys);
  }, [handleSetPage, closeModal, goBack, goForward]);

  let content;
  if (page.name === 'starter-anima') {
    return <div className="App"><GlobalStyles /><StarterAnimaPage setPage={handleSetPage} /></div>;
  }

  // --- ROUTING LOGIC ---
  switch (page.name) {
    case 'home': content = <HomePage setPage={handleSetPage} />; break;
    case 'about': content = <AboutPage />; break;
    case 'services': content = <ServicesPage setPage={handleSetPage} />; break;
    case 'service-detail': content = <ServiceDetailPage serviceId={page.params?.id || 'talim'} setPage={handleSetPage} />; break;
    case 'service-checkout': content = <ServiceCheckoutPage serviceId={page.params?.id || 'talim'} userInfo={{ email: verifiedEmail }} setPage={handleSetPage} />; break;
    case 'account-service-detail': content = <AccountServiceDetailPage member={accountMember} serviceIndex={page.params?.index || 0} setPage={handleSetPage} />; break;
    case 'gallery': content = <GalleryPage setPage={handleSetPage} />; break;
    case 'events': content = <EventsPage setPage={handleSetPage} setSelectedEvent={setSelectedEvent} />; break;
    case 'event-register': content = <EventRegisterPage event={selectedEvent} setPage={handleSetPage} setModalState={setModalState} />; break;
    case 'contact': content = <ContactPage setModalState={setModalState} />; break;
    case 'register': content = <RegisterPage setPage={handleSetPage} setModalState={setModalState} params={page.params} />; break;
    case 'faq': content = <FAQPage setPage={handleSetPage} />; break;
    case 'help': content = <HelpPage setPage={handleSetPage} />; break;
    case 'verify': 
      content = (
        <VerifyCodePage 
          defaultEmail={page.params?.email || ''} 
          phone={page.params?.phone || ''}
          name={page.params?.name || ''}
          onVerified={(email) => { setVerifiedEmail(email); handleSetPage({ name: 'account' }); }} 
          setPage={handleSetPage} 
        />
      ); 
      break;
    case 'account': content = <MyAccountPage defaultEmail={verifiedEmail} setPage={handleSetPage} onLoaded={setAccountMember} />; break;
    case 'reset-password': content = <ResetPasswordPage setPage={handleSetPage} />; break;
    case 'dashboard': content = <Dashboard setPage={handleSetPage} />; break;
    case 'booking-selection': 
      content = (
        <BookingSelectionPage 
           serviceId={page.params?.serviceId} 
           setPage={handleSetPage} 
           previousPageParams={page.params} 
        />
      ); 
      break;
    case 'offers': content = <OffersPage setPage={handleSetPage} />; break;
    default: content = <HomePage setPage={handleSetPage} />;
  }

  const isFullScreenPage = ['owner', 'dashboard', 'booking-selection'].includes(page.name);
  const containerClass = isFullScreenPage ? 'animate-dashboard' : 'animate-fadeUp';

  return (
    <div className="App">
      <GlobalStyles />
      <Modal show={modalState.show} title={modalState.title} message={modalState.message} content={modalState.content} type={modalState.type} onClose={closeModal} />
      
      {!isFullScreenPage && <MarqueeBar />}
      {!isFullScreenPage && <Header setPage={handleSetPage} activePage={page.name} />}
      
      <main className={containerClass} key={page.name}>
        {content}
      </main>
      
      {!isFullScreenPage && <Footer setPage={handleSetPage} />}

      <div className={`fab-container ${settingsOpen ? 'open' : ''}`}>
        <button className="fab-item" onClick={scrollToTop} data-tooltip="Scroll Top">⬆️</button>
        <button className="fab-item" onClick={toggleDarkMode} data-tooltip="Dark Mode">{darkMode ? '☀️' : '🌙'}</button>
        <button className="fab-item" onClick={showShortcuts} data-tooltip="Help">❓</button>
        <button className={`fab-main ${settingsOpen ? 'active' : ''}`} onClick={() => setSettingsOpen(!settingsOpen)}>⚙️</button>
      </div>
    </div>
  );
}

export default App;